import React, { useContext, useEffect, useState, useMemo, useRef } from "react";
import AccountNavBar from "../AccountNavBar.js";
import { checkFetchAlgoliaData, firestore } from "../../firebase.config.js";
import { useScreenTransition } from "../../Hooks/useScreenTransition.js";
import { Configure, useSortBy, useHits, useInstantSearch, useSearchBox } from "react-instantsearch-hooks-web";
import { ReactComponent as DOWN } from "../../assets/icons/filter-down.svg";
import { ReactComponent as UP } from "../../assets/icons/filter-up.svg";
import { ReactComponent as CLIENT } from "../../assets/icons/placed-by.svg";
import { ReactComponent as DATE } from "../../assets/icons/Date.svg";
import { ReactComponent as SORT_ASC } from "../../assets/icons/sort-asc.svg";
import { ReactComponent as SORT_DSC } from "../../assets/icons/sort-dsc.svg";
import { ReactComponent as SORTBLACK } from "../../assets/icons/sort-black.svg";
import { ReactComponent as ARROWTOPRIGHT } from "../../assets/icons/arrow.svg";
import NoData from "../../NewComponents/NoData";

import { AuthContext } from "../../Context/AuthContext.js";
import { TransitionContext } from "../../Context/TransitionContext.js";
import { DataContext } from "../../Context/DataContext.js";

import TitleWithSearch from "../../Components/ui/TitleWithSearch.js";
import Clientsfilter from "../../Screens/Account/OrdersComponents/Clientsfilter.js";
import NewButton from "../NewButton.js";
import DateFilter from "../../Screens/Account/OrdersComponents/DateFilter.js";
import SendNotificationModal from "./SendNotificationModal";
import "../../Screens/Account/TeamMembers.css"

const AdminNotificationsComponent = ({ windowSize, notifications }) => {

  const [currentPage, setCurrentPage] = useState(1);
  const [selectedClients, setSelectedClients] = useState([]);
  const [selectedClientIds, setSelectedClientIds] = useState([]);
  const [filterStartTimestamp, setFilterStartTimestamp] = useState(null);
  const [filterEndTimestamp, setFilterEndTimestamp] = useState(null);
  const [datePeriod, setDatePeriod] = useState("custom");
  const [clients, setClients] = useState([]);
  const [showFilter, setShowFilter] = useState("");
  const [filterSearch, setFilterSearch] = useState("");
  const [openSendNotificationModal, setOpenSendNotificationModal] = useState(false);
  const [openEditNotificationModal, setOpenEditNotificationModal] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [loading, setLoading] = useState(true);
  const { hits } = useHits();
  const { status } = useInstantSearch();
  const { query } = useSearchBox();

  const { setColor } = useContext(TransitionContext);
  const { userData } = useContext(DataContext);
  const { signedIn } = useContext(AuthContext);

  const items = [
    { Label: "Default", value: "notifications" },
    { Label: "Type asc", value: "notification_type_asc" },
    { Label: "Type dsc", value: "notification_type_dsc" },
    { Label: "Notifications Date asc", value: "notification_time_asc" },
    { Label: "Notifications Date dsc", value: "notification_time_dsc" },
    { Label: "View asc", value: "notification_view_asc" },
    { Label: "View dsc", value: "notification_view_dsc" },
    { Label: "Click asc", value: "notification_click_asc" },
    { Label: "Click dsc", value: "notification_click_dsc" },
  ];
  const { currentRefinement, refine } = useSortBy({ items: items, defaultRefinement: "notification_time_dsc" });

  const { transitionTo } = useScreenTransition();
  const itemsPerPage = 10;

  useEffect(() => {
    if (status === "loading" || status === "stalled") {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [status])

  useEffect(() => {
    setColor(true);
  }, [setColor]);

  useEffect(() => {
    if (!signedIn || (userData && userData.role === "client")) {
      return transitionTo("/");
    }
  }, [signedIn, userData]);

  useEffect(() => {
    if (signedIn && userData) {

      firestore
        .collection("users")
        .where("role", "==", "client")
        .orderBy("name", "asc")
        .get()
        .then((qSnap) => {
          if (!qSnap.empty) {
            setClients(
              qSnap.docs.map((doc) => ({ ...doc.data(), id: doc.id }))
            );
          }
        })
        .catch((error) => {
          console.error("Error fetching clients:", error);
        });
    }
  }, [signedIn, userData]);

  useEffect(() => {
    if (!selectedClients || selectedClients.length === 0) {
      setSelectedClientIds([]);
      return;
    }

    const fetchClientIds = async () => {
      try {
        const usersRef = firestore.collection("users");
        const MAX_BATCH = 10;
        const allDocs = [];

        for (let i = 0; i < selectedClients.length; i += MAX_BATCH) {
          const batch = selectedClients.slice(i, i + MAX_BATCH);
          const query = usersRef.where("email", "in", batch);
          const snapshot = await query.get();
          snapshot.forEach((doc) => {
            allDocs.push({ id: doc.id, ...doc.data() });
          });
        }

        const ids = allDocs.map((doc) => doc.id);
        setSelectedClientIds(ids);
      } catch (error) {
        console.error("Error fetching clients:", error);
      }
    };

    fetchClientIds();
  }, [selectedClients]);

  const filteredNotifications = useMemo(() => {
    return notifications.filter((n) => {
      if (selectedClientIds.length > 0 && !selectedClientIds.includes(n.clientId)) {
        return false;
      }

      if (filterStartTimestamp || filterEndTimestamp) {
        const notificationDate = new Date(
          n.date.split("/").reverse().join("-")
        );

        const startDate = filterStartTimestamp
          ? new Date(filterStartTimestamp)
          : null;
        const endDate = filterEndTimestamp
          ? new Date(filterEndTimestamp)
          : new Date();

        if (startDate && notificationDate < startDate) return false;
        if (endDate && notificationDate > endDate) return false;
      }

      return true;
    });
  }, [notifications, selectedClients, filterStartTimestamp, filterEndTimestamp]);

  const paginatedNotifications = filteredNotifications.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const formatFilterDate = (date) => {
    return (
      date.getDate() + "/" + (date.getMonth() + 1) + "/" + date.getFullYear()
    );
  };

  const getSelectedDateText = () => {
    if (filterStartTimestamp) {
      if (filterEndTimestamp) {
        return (
          formatFilterDate(new Date(filterStartTimestamp)) +
          " - " +
          formatFilterDate(new Date(filterEndTimestamp))
        );
      } else {
        return (
          formatFilterDate(new Date(filterStartTimestamp)) +
          " - " +
          formatFilterDate(new Date())
        );
      }
    } else {
      return "Date";
    }
  };

  const handleOpenEditModal = (notification) => {
    setSelectedNotification(notification);
    setOpenEditNotificationModal(true);
  };

  const AdminNotificationsTableDataComponent = ({ notifications = [] }) => {
    if (!notifications.length) return <tbody></tbody>;

    return (
      <tbody>
        {notifications.map((notification, index) => {
          const date = new Date(notification?.createdAt);
          const formattedDate = date.toLocaleDateString('en-GB');
          return (
          <tr key={notification.id || index} style={{ cursor: "pointer" }} onClick={(e) => {
              e.preventDefault();
              handleOpenEditModal(notification);
            }}>

            <td>
              <p className="quotewidth accountDetailsLink">
                {notification.type || "-"}
              </p>
            </td>

            <td>
              <p className="quotewidth">
                  {formattedDate}
              </p>
            </td>

            <td>
              <p className="quotewidth overflowText">
                {notification.title || "-"}
              </p>
            </td>

            <td>
              <p className="quotewidth overflowText">
                {notification.description || "-"}
              </p>
            </td>

            <td>
              <p className="quotewidth">{notification.views ?? "-"}</p>
            </td>

            <td>
              <p className="quotewidth">{notification.clicks ?? "-"}</p>
            </td>

            <td style={{ textAlign: "center" }}>
              <ARROWTOPRIGHT onClick={() => handleOpenEditModal(notification)} />
            </td>
          </tr>
          )
        })}
      </tbody>
    );
  };
  const checkAnyFilterSelected = () => {
    if (filterStartTimestamp) {
      return true;
    }
    if (filterEndTimestamp) {
      return true;
    }
    return false;
  };

  return (

    <>
      <Configure hitsPerPage={10} />

      <AccountNavBar
        windowSize={windowSize}
        selectedTab="admin-notifications"
        accountMainPadding={false}
        admin={true}
        showButton={true}
      >
        <TitleWithSearch
          title="Notifications"
          placeholder="Search a Notification"
          onSearch={(val) => setFilterSearch(val)}
        />
        <div className="w-full flex lg:flex-row lg:justify-between items-center flex-col gap-4 md:px-5 px-2">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 lg:w-fit w-full gap-4">

            <div className="relative">
              <div
                className="accountDetailsFilter"
                style={{
                  borderColor:
                    showFilter === "date" ||
                      filterStartTimestamp ||
                      filterEndTimestamp
                      ? "#12ABBF"
                      : "#E6E7E9",
                }}
                onClick={(e) => {
                  setShowFilter((f) => (f === "date" ? "" : "date"));
                }}
              >
                <DATE />
                <p className="accountDetailsFilterText">
                  {getSelectedDateText()}
                </p>
                {showFilter === "date" ? <UP /> : <DOWN />}
              </div>
              {showFilter === "date" && (
                <DateFilter
                  setShowModal={setShowFilter}
                  startTimestamp={filterStartTimestamp}
                  setStartTimestamp={setFilterStartTimestamp}
                  endTimestamp={filterEndTimestamp}
                  setEndTimestamp={setFilterEndTimestamp}
                  period={datePeriod}
                  setPeriod={setDatePeriod}
                />
              )}
            </div>

            <div className="relative">
              <div
                className="accountDetailsFilter"
                style={{
                  borderColor:
                    showFilter === "name" || selectedClients.length > 0
                      ? "#12ABBF"
                      : "#E6E7E9",
                }}
                onClick={(e) => {
                  setShowFilter((f) => (f === "name" ? "" : "name"));
                }}
              >
                <CLIENT />
                <p className="accountDetailsFilterText">
                  Client{" "}
                  {selectedClients.length > 0
                    ? ` (${selectedClients.length}) `
                    : ""}
                </p>
                {showFilter === "name" ? <UP /> : <DOWN />}
              </div>
              {showFilter === "name" && (
                <Clientsfilter
                  setShowModal={setShowFilter}
                  clients={clients}
                  setSelectedClients={setSelectedClients}
                  selectedClients={selectedClients}
                />
              )}
            </div>
          </div>

          <div className="lg:justify-start justify-end lg:block flex lg:w-auto w-full">
            <NewButton
              text="Send New Notification"
              icon={false}
              width="150px"
              backgroundColor="#12ABBF"
              onClick={() => {
                setOpenSendNotificationModal(true);
              }}
            />
          </div>
        </div>

        <div className="accountDetailsScroll w-full md:w-[calc(100vw-270px)]" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          {loading ? <NoData loadingText={"notifications"} loading={true} showBtn={false} type="notification" /> :
            (checkFetchAlgoliaData() ? hits?.length === 0 : filteredNotifications?.length === 0) &&
            <NoData
              type="notification"
              text={checkAnyFilterSelected() || query !== "" ? "Please select another filter" : ""}
              title={checkAnyFilterSelected() || query !== "" ? "No Notifications Matching This Filter" : "You Don't Have Any Notifications Yet"}
              showBtn={false}
            />}
          {filteredNotifications.length > 0 && (!loading) && (
            <>
          <table className="accountDetailsTable">
            <thead>
              <tr>
                <th>
                  <div className="accountData">
                    <p className="accountDetailsHeading">Type</p>
                    {currentRefinement === "notification_type_dsc" ?
                      <SORT_DSC className="sortIcon" onClick={() => refine("notification_type_asc")} />
                      : currentRefinement === "order_requestBy_asc" ?
                        <SORT_ASC className="sortIcon" onClick={() => refine("notification_type_dsc")} />
                        : <SORTBLACK className="sortIcon" onClick={() => refine("notification_type_dsc")} />
                    }
                  </div>
                </th>
                <th>
                  <div className="accountData">
                    <p className="accountDetailsHeading">Date</p>
                    {currentRefinement === "notification_time_dsc" ?
                      <SORT_DSC className="sortIcon" onClick={() => refine("notification_time_asc")} />
                      : currentRefinement === "order_time_asc" ?
                        <SORT_ASC className="sortIcon" onClick={() => refine("notification_time_dsc")} />
                        : <SORTBLACK className="sortIcon" onClick={() => refine("notification_time_dsc")} />
                    }
                  </div>
                </th>
                <th>
                  <div className="accountData">
                    <p className="accountDetailsHeading">Title</p>
                  </div>
                </th>
                <th>
                  <div className="accountData">
                    <p className="accountDetailsHeading">Dlescription</p>
                  </div>
                </th>
                <th>
                  <div className="accountData">
                    <p className="accountDetailsHeading">Views</p>
                    {currentRefinement === "notification_view_dsc" ?
                      <SORT_DSC className="sortIcon" onClick={() => refine("notification_view_asc")} />
                      : currentRefinement === "order_time_asc" ?
                        <SORT_ASC className="sortIcon" onClick={() => refine("notification_view_dsc")} />
                        : <SORTBLACK className="sortIcon" onClick={() => refine("notification_view_dsc")} />
                    }
                  </div>
                </th>
                <th>
                  <div className="accountData">
                    <p className="accountDetailsHeading">Clicks</p>
                    {currentRefinement === "notification_click_dsc" ?
                      <SORT_DSC className="sortIcon" onClick={() => refine("notification_click_asc")} />
                      : currentRefinement === "order_time_asc" ?
                        <SORT_ASC className="sortIcon" onClick={() => refine("notification_click_dsc")} />
                        : <SORTBLACK className="sortIcon" onClick={() => refine("notification_click_dsc")} />
                    }
                  </div>
                </th>
              </tr>
            </thead>
            <AdminNotificationsTableDataComponent notifications={paginatedNotifications} />
              </table>
              <div className="flex justify-center mt-4 items-center">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                  disabled={currentPage === 1}
                  className={`flex items-center px-4 py-1 font-medium bg-primary text-white rounded-md hover:bg-primary/70 transition ${currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                >
                  Previous
                </button>

                <span className="mx-2">Page {currentPage}</span>

                <button
                  onClick={() =>
                    setCurrentPage((p) =>
                      p < Math.ceil(filteredNotifications.length / itemsPerPage) ? p + 1 : p
                    )
                  }
                  disabled={filteredNotifications.length <= itemsPerPage || currentPage >= Math.ceil(filteredNotifications.length / itemsPerPage)}
                  // disabled={currentPage === Math.ceil(filteredNotifications.length / itemsPerPage)}
                  className={`flex items-center px-4 py-1 font-medium bg-primary text-white rounded-md hover:bg-primary/70 transition ${filteredNotifications.length <= itemsPerPage || currentPage >= Math.ceil(filteredNotifications.length / itemsPerPage)
                    ? 'opacity-50 cursor-not-allowed'
                    : ''
                    }`}
                >
                  Next
                </button>
              </div>
            </>
          )}
        </div>
      </AccountNavBar>

      {(openSendNotificationModal || openEditNotificationModal) &&
        <SendNotificationModal
          onClose={() => {setOpenSendNotificationModal(false); setOpenEditNotificationModal(false);}}
          {...(openEditNotificationModal && {
            setPreview: true,
            notificationData: selectedNotification,
          })}
        />
      }
    </>
  );
};

export default AdminNotificationsComponent;
